import { useCallback } from 'react';
import { useConfirmationModal, useSnackbars } from '@customerio/pluma-components/react';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { useDeleteCollection } from '@/services/db/collection/mutations';
import type { Collection } from '@/services/db/collection/entity';
import { useAnalytics } from '@/services/analytics';
import { getCollectionAnalyticsProperties } from './collection-analytics';

type UseCollectionDeleteOptions = {
  workspaceId: number;
  onDeleted?: () => void;
};

/**
 * The confirm/mutate/notify flow behind every Delete collection affordance, plus the
 * in-use explanation those surfaces show. Shared so the list row action and the detail
 * header can't drift on copy or on the taxonomy noun.
 */
export function useCollectionDelete({ workspaceId, onDeleted }: UseCollectionDeleteOptions) {
  const taxonomy = useProductTaxonomy();
  const snackbars = useSnackbars();
  const confirm = useConfirmationModal();
  const deleteCollection = useDeleteCollection({ workspaceId });
  const analytics = useAnalytics();

  const requestDelete = useCallback(
    async (collection: Collection) => {
      void analytics.track('collections', 'deleteStarted', getCollectionAnalyticsProperties(collection));
      const confirmed = await confirm({
        isCritical: true,
        title: `Delete ${collection.name}?`,
        message: `Deleting the ${collection.name} collection will permanently delete its data. This cannot be undone.`,
        confirmLabel: 'Delete collection',
        cancelLabel: 'Cancel',
      });
      if (!confirmed) return;
      try {
        await deleteCollection.mutateAsync({ id: collection.id });
        snackbars.create({ variant: 'success', message: `${collection.name} deleted.` });
        onDeleted?.();
      } catch {
        snackbars.create({ variant: 'critical', message: "Collection couldn't be deleted. Try again." });
      }
    },
    [analytics, confirm, deleteCollection, onDeleted, snackbars],
  );

  return {
    requestDelete,
    inUseTooltip: `Collections that are used in a ${taxonomy.term('campaigns.singular')} cannot be deleted.`,
  };
}
